import React from 'react';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Cell,
    LabelList,
} from 'recharts';
import { Box, Text, Flex, useTheme } from '@chakra-ui/react';
import CustomBarCursor from '../UI/CustomBarCursor';

const fmtBRL = (v) =>
    typeof v === 'number'
        ? v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
        : v;

const fmtK = (v) =>
    v >= 1000 ? `R$${(v / 1000).toFixed(0)}k` : `R$${Number(v).toFixed(0)}`;

// Tick colorido: nome da tag na cor da tag
const CustomYAxisTick = ({ x, y, payload, chartData, axisColor }) => {
    const entry = chartData.find((d) => d.nome === payload.value);
    return (
        <g transform={`translate(${x},${y})`}>
            <text
                x={0}
                y={0}
                dy={4}
                textAnchor="end"
                fill={entry?.cor || axisColor}
                fontSize={12}
                fontWeight="bold"
            >
                {payload.value}
            </text>
        </g>
    );
};

// Tooltip glassmorphism na cor da tag
const InadimplenciaTagTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const entry = payload[0]?.payload;
    if (!entry) return null;

    return (
        <Box
            p={3}
            bg="rgba(0,0,0,0.4)"
            borderRadius="md"
            border="1px solid"
            borderColor={entry.cor || 'secondary'}
            backdropFilter="blur(8px)"
            boxShadow={`0 0 10px ${entry.cor || 'rgba(52,211,103,0.5)'}55`}
            zIndex={99}
            minW="220px"
            maxW="300px"
        >
            <Text color={entry.cor || 'secondary'} fontWeight="bold" mb={2} fontSize="sm">
                {entry.nome}
            </Text>
            <Text color="white" fontSize="sm" mb={1}>
                Total em Aberto: <strong>{fmtBRL(entry.total)}</strong>
            </Text>
            <Text color="gray.400" fontSize="xs" mb={entry.produtos?.length ? 3 : 0}>
                {entry.quantidade} registro(s)
            </Text>
            {entry.produtos?.length > 0 && (
                <>
                    <Text
                        color="gray.400"
                        fontSize="xs"
                        fontWeight="semibold"
                        textTransform="uppercase"
                        letterSpacing="wider"
                        mb={1}
                        borderTop="1px solid"
                        borderColor="whiteAlpha.200"
                        pt={2}
                    >
                        Por Produto
                    </Text>
                    {entry.produtos.map((p, i) => (
                        <Flex key={i} justify="space-between" align="center" mb={0.5}>
                            <Text color="gray.300" fontSize="xs" noOfLines={1} flex="1" mr={2}>
                                {p.nome}
                            </Text>
                            <Text color="white" fontSize="xs" fontWeight="bold" flexShrink={0}>
                                {fmtBRL(p.total)}
                            </Text>
                        </Flex>
                    ))}
                </>
            )}
        </Box>
    );
};

const InadimplenciaPorTagChart = ({ data = [] }) => {
    const theme = useTheme();
    const AXIS_COLOR = theme.colors?.gray?.[400] || '#A0AEC0';

    if (!data || data.length === 0) {
        return (
            <Flex justify="center" align="center" h="100%">
                <Text color="gray.500">Nenhum dado disponível por tag.</Text>
            </Flex>
        );
    }

    const total = data.reduce((s, d) => s + d.total, 0);
    const chartData = data.map((d) => ({
        ...d,
        pct: total > 0 ? ((d.total / total) * 100).toFixed(1) : '0',
    }));

    return (
        <ResponsiveContainer width="100%" height="100%">
            <BarChart
                data={chartData}
                layout="vertical"
                margin={{ top: 10, right: 60, left: 10, bottom: 10 }}
                barCategoryGap="20%"
            >
                <CartesianGrid
                    horizontal={false}
                    strokeDasharray="3 3"
                    stroke="rgba(255, 255, 255, 0.1)"
                />
                <XAxis
                    type="number"
                    tickFormatter={fmtK}
                    tick={{ fill: AXIS_COLOR, fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                />
                <YAxis
                    type="category"
                    dataKey="nome"
                    axisLine={false}
                    tickLine={false}
                    width={100}
                    tick={(props) => (
                        <CustomYAxisTick
                            {...props}
                            chartData={chartData}
                            axisColor={AXIS_COLOR}
                        />
                    )}
                />
                <Tooltip
                    content={<InadimplenciaTagTooltip />}
                    cursor={<CustomBarCursor />}
                    wrapperStyle={{ zIndex: 99 }}
                />
                <Bar dataKey="total" name="Em Aberto" radius={[0, 6, 6, 0]} maxBarSize={44}>
                    {chartData.map((entry, i) => (
                        <Cell key={i} fill={entry.cor || '#FC8181'} fillOpacity={0.9} />
                    ))}
                    <LabelList
                        dataKey="pct"
                        position="right"
                        formatter={(v) => `${v}%`}
                        style={{ fill: AXIS_COLOR, fontSize: 11 }}
                    />
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    );
};

export default InadimplenciaPorTagChart;
